/**
 * ImportLotesModal — importación masiva de LOTES por Excel para un desarrollo
 * de tipo Lotes/Macrolotes. Backend: /api/developments/{id}/units/import/*.
 *
 * Flujo: 0 subir (plantilla + archivo + modo) → 1 vista previa (valida,
 * outliers, responsabilidad) → 2 resultado. Reusa la mecánica del importador
 * de propiedades, acotado a dev_units de UN desarrollo.
 */
import { useState, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, FileDown, Loader2, CheckCircle2, AlertTriangle, ArrowLeft, Package, X } from 'lucide-react';

const fmtPrice = (v) => {
  if (v === null || v === undefined || v === '' || isNaN(Number(v))) return '-';
  try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(Number(v)); }
  catch { return `$${Number(v).toLocaleString('es-MX')}`; }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function ImportLotesModal({ open, onClose, onComplete, developmentId, unitType = 'lote', developmentName = '' }) {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('append'); // append | replace
  const [file, setFile] = useState(null);
  const [parseRes, setParseRes] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [confirmedOutliers, setConfirmedOutliers] = useState(new Set());
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);
  const [executeRes, setExecuteRes] = useState(null);
  const fileInputRef = useRef(null);

  const tipo = (parseRes?.unit_type || unitType);
  const esMacro = tipo === 'macrolote';
  const unidadPlural = esMacro ? 'macrolotes' : 'lotes';
  const nivelLabel = esMacro ? 'Sección' : 'Manzana';

  const reset = () => {
    setStep(0); setMode('append'); setFile(null); setParseRes(null);
    setConfirmedOutliers(new Set()); setResponsibilityAccepted(false); setExecuteRes(null);
  };
  const handleClose = () => { reset(); onClose?.(); };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const r = await api.get(`/developments/${developmentId}/units/import/template`, { responseType: 'blob' });
      downloadBlob(r.data, esMacro ? 'plantilla_macrolotes.xlsx' : 'plantilla_lotes.xlsx');
      toast.success('Plantilla descargada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al descargar la plantilla'); }
    finally { setTemplateLoading(false); }
  };

  const handleFileSelected = async (selected) => {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.xlsx') && !selected.name.toLowerCase().endsWith('.xlsm')) {
      toast.error('Solo se aceptan archivos .xlsx'); return;
    }
    setFile(selected); setParsing(true);
    try {
      const fd = new FormData(); fd.append('file', selected);
      const r = await api.post(`/developments/${developmentId}/units/import/parse`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setParseRes(r.data); setStep(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al analizar el archivo'); setFile(null); }
    finally { setParsing(false); }
  };

  const toggleOutlier = (rowIdx) => {
    setConfirmedOutliers((prev) => { const n = new Set(prev); n.has(rowIdx) ? n.delete(rowIdx) : n.add(rowIdx); return n; });
  };
  const allOutliersConfirmed = useMemo(() => {
    if (!parseRes?.outlier_rows?.length) return true;
    return parseRes.outlier_rows.every((o) => confirmedOutliers.has(o.row));
  }, [parseRes, confirmedOutliers]);
  const canExecute = responsibilityAccepted && allOutliersConfirmed && (parseRes?.valid_rows || 0) > 0;

  const handleExecute = async () => {
    if (!parseRes) return;
    setExecuting(true);
    try {
      const r = await api.post(`/developments/${developmentId}/units/import/execute`, {
        session_id: parseRes.session_id,
        mode,
        confirmed_outliers: Array.from(confirmedOutliers),
        responsibility_accepted: true,
      });
      setExecuteRes(r.data); setStep(2);
    } catch (e) {
      setExecuteRes({ success: false, error: e.response?.data?.detail || 'Error al importar' }); setStep(2);
    } finally { setExecuting(false); }
  };

  const inputCls = 'bg-black/20 border border-white/10 rounded text-xs';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-[#0a0c12] border-white/[0.06] max-h-[90vh] overflow-y-auto" data-testid="import-lotes-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2"><Package className="w-5 h-5 text-violet-400" />Importar {unidadPlural} desde Excel</DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">{developmentName ? `${developmentName} · ` : ''}Sube un .xlsx con un {esMacro ? 'macrolote' : 'lote'} por fila. Cada uno con su superficie, uso y precio.</DialogDescription>
        </DialogHeader>

        {/* STEP 0 — plantilla + archivo + modo */}
        {step === 0 && (
          <div className="space-y-4" data-testid="import-lotes-step-0">
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
              <p className="text-xs text-white/70 mb-2">1. Descarga la plantilla, llénala (un {esMacro ? 'macrolote' : 'lote'} por fila) y súbela.</p>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} disabled={templateLoading} className="text-xs border-white/10 hover:bg-white/5" data-testid="import-lotes-template-btn">
                {templateLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />}Descargar plantilla .xlsx
              </Button>
            </div>

            <div>
              <Label className="text-[11px] text-white/60 mb-1.5 block">2. ¿Qué hacer con los {unidadPlural} que ya tiene el desarrollo?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode('append')} className={`p-2.5 rounded-lg border text-left transition-all ${mode === 'append' ? 'bg-violet-500/15 border-violet-500/40 text-white' : 'bg-black/20 border-white/10 text-white/50'}`} data-testid="import-lotes-mode-append">
                  <p className="text-xs font-semibold">Agregar</p>
                  <p className="text-[10px] text-muted-foreground">Suma los nuevos a los existentes</p>
                </button>
                <button type="button" onClick={() => setMode('replace')} className={`p-2.5 rounded-lg border text-left transition-all ${mode === 'replace' ? 'bg-amber-500/15 border-amber-500/40 text-white' : 'bg-black/20 border-white/10 text-white/50'}`} data-testid="import-lotes-mode-replace">
                  <p className="text-xs font-semibold">Reemplazar</p>
                  <p className="text-[10px] text-muted-foreground">Borra los actuales y deja solo los del Excel</p>
                </button>
              </div>
              {mode === 'replace' && (
                <div className="mt-2 flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/20">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-300">Reemplazar elimina los {unidadPlural} actuales de este desarrollo. Los que estén vendidos o apartados NO se borran (se rechaza y te avisa).</p>
                </div>
              )}
            </div>

            <div>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => handleFileSelected(e.target.files?.[0])} data-testid="import-lotes-file-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={parsing} className="w-full h-24 rounded-lg border-2 border-dashed border-white/10 hover:border-violet-500/30 flex flex-col items-center justify-center gap-1 transition-colors" data-testid="import-lotes-upload-btn">
                {parsing ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
                <span className="text-[11px] text-muted-foreground">{parsing ? 'Analizando archivo...' : '3. Sube tu archivo .xlsx'}</span>
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — vista previa */}
        {step === 1 && parseRes && (
          <div className="space-y-3" data-testid="import-lotes-step-1">
            <div className="grid grid-cols-3 gap-2">
              {[['Válidos', parseRes.valid_rows, 'text-emerald-400'], ['Con error', parseRes.error_rows?.length || 0, 'text-red-400'], ['Modo', mode === 'replace' ? 'Reemplazar' : 'Agregar', 'text-violet-300']].map(([l, v, c]) => (
                <div key={l} className="p-2 rounded bg-white/[0.02] border border-white/5 text-center"><p className={`text-base font-bold ${c}`}>{v}</p><p className="text-[9px] text-muted-foreground">{l}</p></div>
              ))}
            </div>

            {parseRes.unknown_columns?.length > 0 && (
              <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-300">Columnas que ignoré (no reconocidas): {parseRes.unknown_columns.join(', ')}</div>
            )}
            {parseRes.error_rows?.length > 0 && (
              <div className="p-2 rounded bg-red-500/10 border border-red-500/20 text-[10px] text-red-300">
                {parseRes.error_rows.slice(0, 5).map((er) => <div key={er.row}>Fila {er.row}: {er.error}</div>)}
                {parseRes.error_rows.length > 5 && <div>… y {parseRes.error_rows.length - 5} más</div>}
              </div>
            )}

            {parseRes.preview?.length > 0 && (
              <div className="overflow-x-auto rounded border border-white/5">
                <table className="w-full text-[10px]" data-testid="import-lotes-preview-table">
                  <thead><tr className="text-muted-foreground border-b border-white/5 bg-white/[0.02]">
                    <th className="text-left py-1.5 px-2">{esMacro ? 'Macrolote' : 'Lote'}</th><th className="text-left py-1.5 px-2">{nivelLabel}</th><th className="text-right py-1.5 px-2">m²</th><th className="text-left py-1.5 px-2">Uso</th><th className="text-right py-1.5 px-2">Precio</th>
                  </tr></thead>
                  <tbody>{parseRes.preview.map((p) => (
                    <tr key={p.row} className="border-b border-white/[0.03]"><td className="py-1 px-2 text-white">{p.data.label}</td><td className="py-1 px-2 text-muted-foreground">{p.data.floor ?? '-'}</td><td className="py-1 px-2 text-right text-white">{p.data.area_m2 || '-'}</td><td className="py-1 px-2 text-muted-foreground">{p.data.uso || '-'}</td><td className="py-1 px-2 text-right text-emerald-400">{fmtPrice(p.data.price)}</td></tr>
                  ))}</tbody>
                </table>
                <p className="text-[9px] text-muted-foreground px-2 py-1">Vista previa de {parseRes.preview.length} de {parseRes.valid_rows}. Se importan todos los válidos.</p>
              </div>
            )}

            {parseRes.outlier_rows?.length > 0 && (
              <div className="p-2 rounded bg-amber-500/[0.06] border border-amber-500/20">
                <p className="text-[10px] text-amber-300 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Precios fuera de rango — confirma cada uno:</p>
                {parseRes.outlier_rows.map((o) => (
                  <label key={o.row} className="flex items-center gap-2 text-[10px] text-white/70 py-0.5 cursor-pointer">
                    <input type="checkbox" checked={confirmedOutliers.has(o.row)} onChange={() => toggleOutlier(o.row)} data-testid={`import-lotes-outlier-${o.row}`} />
                    Fila {o.row}: {o.title} — {o.price} ({o.reason})
                  </label>
                ))}
              </div>
            )}

            <label className="flex items-start gap-2 text-[11px] text-white/70 cursor-pointer">
              <input type="checkbox" checked={responsibilityAccepted} onChange={(e) => setResponsibilityAccepted(e.target.checked)} className="mt-0.5" data-testid="import-lotes-responsibility" />
              Confirmo que revisé los datos y autorizo crear estos {unidadPlural} en el desarrollo.
            </label>

            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setStep(0); setParseRes(null); }} className="text-xs" data-testid="import-lotes-back"><ArrowLeft className="w-3 h-3 mr-1" />Atrás</Button>
              <Button onClick={handleExecute} disabled={!canExecute || executing} className="flex-1 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold" data-testid="import-lotes-execute">
                {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}{mode === 'replace' ? 'Reemplazar con' : 'Importar'} {parseRes.valid_rows} {unidadPlural}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — resultado */}
        {step === 2 && executeRes && (
          <div className="space-y-4 text-center py-4" data-testid="import-lotes-step-2">
            {executeRes.success ? (
              <>
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                <div>
                  <p className="text-sm font-semibold text-white">{executeRes.imported} {unidadPlural} importados</p>
                  {executeRes.skipped > 0 && <p className="text-[11px] text-amber-400">{executeRes.skipped} omitidos (outliers no confirmados)</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">Modo: {executeRes.mode === 'replace' ? 'reemplazo' : 'agregado'}</p>
                </div>
                <Button onClick={() => { onComplete?.(); handleClose(); }} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="import-lotes-finish">Ver el mapa de {unidadPlural}</Button>
              </>
            ) : (
              <>
                <X className="w-12 h-12 text-red-400 mx-auto" />
                <p className="text-sm text-red-300">{executeRes.error}</p>
                <Button variant="outline" onClick={() => setStep(0)} className="text-xs border-white/10" data-testid="import-lotes-retry">Reintentar</Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
